// The product screens: the landing page's gallery and the /tour page.
//
// Founder, 2026-09-29, on the built pick-A page: "So are we removing all the
// feature level screen shots from the landing page? They can still be there.
// just that the most important things only appear first." Offered A (six in
// a gallery below planning, plus a tour page with all of them), B (all
// fourteen below planning) or C (as built), they answered "A" (ADR 0022).
//
// Every panel is the replica the old feature rows used (components/landing/
// v2), each a faithful copy of a shipped screen. The paragraphs are those
// rows' words, cleaned: no dashes, no retired names, no claim the product
// no longer makes. Guides are read from the paper modules, so a link can
// never name a slug that does not exist. `cta` ids are registered in
// content/landingIds.js (and its Python twin) for the gallery's six.
//
// Left out, with the reason:
//   - Visits (VisitsPanel): it shows August dates from the retired weekly
//     calendar; the calendar itself is retired.
//   - The film band: the one film is linked at /#film, not played on a page.
//   - The stats band, comparison table, parent band, mission and trust
//     sections: none is a product screen.
//   - The guides preview (two sample paper cards, one of them the visits
//     guide): the Guides row is words and a link, `textOnly`.

import { BUILD_NAMES } from './buildNames';
import { MAJORS_LABEL, SUMMER_PROGRAM_COUNT } from '../constants/catalog';
import tellStratia from '../data/resources/tell-stratia-everything';
import strategy from '../data/resources/building-your-application-strategy';
import instantRead from '../data/resources/instant-read-deep-read';
import launchpad from '../data/resources/the-launchpad';
import majorTrap from '../data/resources/boilerplate-major-trap';
import patentPending from '../data/resources/patent-pending-on-your-application';
import depth from '../data/resources/depth-over-breadth';
import safetySchool from '../data/resources/what-is-a-safety-school';
import summers from '../data/resources/making-summer-count';
import realDecisions from '../data/resources/reading-real-decisions';
import essayPaper from '../data/resources/the-essay-only-you-can-write';
import roadmapPaper from '../data/resources/how-stratia-builds-roadmap';
import sharePaper from '../data/resources/share-your-application';
import agentsPaper from '../data/resources/how-stratia-works-with-ai-agents';

// `paper` is the guide (its slug).
export const TOUR_PANELS = [
    {
        key: 'profile', eyebrow: 'Your profile', title: 'Tell Stratia everything. Then see yourself whole.',
        body: "The thing you loved at ten. The certificate in a drawer. The club that is not on any transcript. Type it, photograph it, or just tell Astra, and it all lands in one record. The Read tab shows you what an admissions reader would see: you, on one page.",
        paper: tellStratia,
    },
    {
        key: 'year', eyebrow: 'Your year', title: 'The app meets you at your year.',
        body: "The same journey re-paces to whoever shows up. A freshman gets the four-year head start; a senior gets this season's moves, with the phase card at the top of the app. Steps check themselves off from your real data, a saved list or odds actually computed, never for just visiting a page.",
        paper: strategy,
    },
    {
        key: 'read', eyebrow: 'The engine', title: 'Watch the read happen.',
        body: "Your record in, the school's own published bar beside it, and the plan out. The same inputs always give the same read, shown here with UCLA's real numbers.",
        paper: instantRead,
    },
    {
        key: 'launchpad', eyebrow: 'What each school weighs', title: 'See what each school actually weighs, and where you stand on it.',
        body: "Every college publishes what it counts and what it ignores. Stratia reads that from the school's own Common Data Set, puts your record beside it factor by factor, and tells you which bars you already clear, before you spend a season on the wrong thing.",
        paper: launchpad,
    },
    {
        key: 'majors', eyebrow: 'Majors before schools', title: 'Map your majors before you pick schools.',
        line: 'Every major the record supports, sized by how many colleges offer it.',
        body: 'Most students default to the same crowded majors everyone applies to, and quietly hurt their own odds. Stratia maps the majors your record actually supports first, then suggests the activities that strengthen that direction.',
        paper: majorTrap, cta: 'gallery_majors',
    },
    {
        key: 'patent', eyebrow: BUILD_NAMES.group, title: BUILD_NAMES.steps.patent.label,
        line: 'A real write-up, with every sentence traced to the answer it came from.',
        body: `It starts from what your profile already shows. ${BUILD_NAMES.home.label}'s seven questions, answered in your own words, become a write-up with every sentence traced to something you said, and a search of the public record shows you what already exists, for you to decide. Then the application, with figures, and the walk through filing it yourselves. Part of the Season Pass.`,
        paper: patentPending, cta: 'gallery_patent',
    },
    {
        key: 'activities', eyebrow: 'Do the right things', title: "Activities chosen from your record, not someone else's checklist.",
        body: 'Tell it what you enjoy and it reads your transcript and activity list, names your spike out loud, and suggests the activities that deepen it. Each comes with the why, the commitment, what it builds on and a first step this week. Every idea can land on your roadmap with a date.',
        paper: depth,
    },
    {
        key: 'list', eyebrow: 'Your list', title: "Build a list that's balanced, not hopeful.",
        line: "One school's own factor list, with where your student stands on each.",
        body: 'Every school in the catalog is scored against your profile and your intended major, with program impaction and in-state rates folded in, then sorted into honest reach, target and safety. The card says why, inline, with nothing to click.',
        paper: safetySchool, cta: 'gallery_list',
    },
    {
        key: 'summers', eyebrow: 'Summers', title: 'A summer that deepens your story.',
        body: `A hand-verified catalog of ${SUMMER_PROGRAM_COUNT} summer programs: real deadlines, cost and selectivity stated plainly, each matched to your major, your activities, whether it pays, and whether your academics put it within reach.`,
        paper: summers,
    },
    {
        key: 'outcomes', eyebrow: 'What really happened', title: 'See what happened to students like you.',
        line: 'Self-reported decisions from applicants with similar grades and scores.',
        body: `Decisions reported by real applicants: GPA, SAT, APs, the round they applied in, and what the college decided. Self-reported, so it is illustrative, and compared with your own record school by school.`,
        paper: realDecisions, cta: 'gallery_outcomes',
    },
    {
        key: 'essays', eyebrow: 'Your story', title: 'Essays, step by step, in your own voice.',
        line: "Each school's real prompt, with starters drawn from your student's record.",
        body: 'The Essay Workshop takes you from a blank page to a finished draft: guided questions to find what to write about, starters built from your own story, and honest feedback as you revise. The coach never writes sentences for you.',
        paper: essayPaper, cta: 'gallery_essays',
    },
    {
        key: 'roadmap', eyebrow: 'Execution', title: 'A roadmap that paces itself to you.',
        line: 'Every dated item on one line, with the busy weeks visible at a glance.',
        body: 'Every dated item sits on one graduation-anchored line: tall stacks are busy days, and deadline-heavy days turn red. It tells you which items close a door if missed, in exactly those words, and a weekly email says what is coming up.',
        paper: roadmapPaper, cta: 'gallery_roadmap',
    },
    {
        key: 'astra', eyebrow: 'Astra', title: 'She reads everything. Before every answer.',
        body: `Astra is built into Stratia. Before she answers, she has read your whole record, the reported decisions at your schools and ${MAJORS_LABEL} majors. She does the work in the app, and she asks before she changes anything.`,
        paper: null,
    },
    {
        key: 'sharing', eyebrow: 'Your team', title: 'Bring your people in, on your terms.',
        body: 'A counselor, a parent, a mentor: each gets a private link by email, no account needed, scoped to exactly the sections you choose. You see how often they looked and when, and revoking is one click.',
        paper: sharePaper,
    },
    {
        key: 'agents', eyebrow: 'Your apps, connected', title: 'Bring your favorite apps. They get real access.',
        body: 'Connect Stratia to Claude, ChatGPT or another assistant you already use, in one step. It reads your real record and can act on it, saving research straight back into the app. Astra needs no connecting: she is already inside.',
        paper: agentsPaper, linkLabel: 'Guide: Connecting your apps',
    },
    {
        key: 'resources', eyebrow: 'Guides', title: 'Answers, ready when you are.',
        body: 'Free guides on how admissions actually works and how Stratia reads it, no account needed, each with its read time up front.',
        paper: null, to: '/resources', linkLabel: 'Browse the guides', textOnly: true,
    },
];

// The landing gallery's six, in order. Chosen to show the page's story in
// the app itself: the Build step the page leads with (File a patent), then
// one screen for each line of "And we plan the rest" that has one (the list,
// majors, roadmap, essays, reported outcomes). Astra and sharing are on the
// tour; they have no line in the planning section above the gallery.
export const GALLERY_KEYS = ['patent', 'list', 'majors', 'roadmap', 'essays', 'outcomes'];

export const GALLERY = {
    title: 'See it in the app',
    sub: 'Real screens from Stratia, with an illustrative student.',
    tourLink: 'See the full tour',
};

export const TOUR = {
    path: '/tour',
    pageTitle: 'Stratia Admissions - A Tour of the App',
    description: 'Every screen in Stratia, one at a time: the profile, what each school weighs, majors, the Build tools, the list, summers, reported outcomes, essays, the roadmap, Astra and sharing. Start free.',
    title: 'A tour of the app',
    sub: 'Every screen, one at a time, with what it does and a free guide for each. The student shown is illustrative.',
};

/**
 * The guide link's words: the paper's own title (so the link says where it
 * goes), cut at its subtitle, so link chrome carries no dash and stays one
 * line: "What Is a Safety School - Really?" reads "What Is a Safety School".
 */
export const guideLabel = (panel) => {
    if (panel.linkLabel) return panel.linkLabel;
    if (!panel.paper) return 'Read the guide';
    return `Guide: ${panel.paper.title.split(/ - |: /)[0]}`;
};

export const guideTo = (panel) => panel.to || (panel.paper ? `/resources/${panel.paper.slug}` : null);
